import React, { useCallback, useEffect, useState } from "react";
import "./App.css";
import { WalletKitProvider } from "@mysten/wallet-kit";

import EnhancedTokenCard from "./EnhancedTokenCard";
import EnhancedTradingInterface from "./EnhancedTradingInterface";
import EnhancedSwapPage from "./EnhancedSwapPage";
import EnhancedNavbar from "./EnhancedNavbar";
import TrendingTicker from "./components/TrendingTicker";
import EnhancedCreateTokenForm from "./EnhancedCreateTokenForm";
import AdminDashboard from "./AdminDashboard";
import EnhancedAdminLogin from "./EnhancedAdminLogin";
import EnhancedUserLogin from "./EnhancedUserLogin";
import EnhancedUserSignup from "./EnhancedUserSignup";
import UserDashboard from "./UserDashboard";
import useAutoLogin from "./useAutoLogin";
import { ThemeProvider } from "./ThemeContext";
import "./PremiumStyles.css";

interface MemeTokenConfig {
  id: string;
  name: string;
  symbol: string;
  description: string;
  image?: string;
  contractPackageId: string;
  bondingCurveId: string;
  metadataId?: string | null; // Made optional since it might not be extracted
  coinType: string;
  decimals: number;
  poolCreated?: boolean;
  poolId?: string | null;
  swapPlatformFeeObj?: string | null;
}

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin + "/api";

// Router functions
function parseRoute(): {
  page: "home" | "create" | "swap" | "token" | "admin" | "admin-login" | "user-login" | "user-signup" | "user-dashboard";
  id?: string
} {
  const path = window.location.pathname;
  if (path.startsWith("/token/")) return { page: "token", id: path.split("/")[2] };
  if (path === "/create") return { page: "create" };
  if (path === "/swaponly") return { page: "swap" };
  if (path === "/admin-login") return { page: "admin-login" };
  if (path === "/admin") return { page: "admin" };
  if (path === "/user-login") return { page: "user-login" };
  if (path === "/user-signup") return { page: "user-signup" };
  if (path === "/user-dashboard") return { page: "user-dashboard" };
  return { page: "home" };
}

function navigate(to: string) {
  window.history.pushState({}, "", to);
  window.dispatchEvent(new Event("pushstate"));
  window.scrollTo(0, 0);
}

// Token Card Component
function TokenCard({ token, onOpen }: { token: MemeTokenConfig; onOpen: () => void }) {
  return (
    <EnhancedTokenCard
      token={{ ...token, image: token.image || "" }}
      onClick={onOpen}
    />
  );
}


// Token Feed Component
function TokenFeed({ tokens, error }: { tokens: MemeTokenConfig[]; error?: boolean }) {
  const [q, setQ] = useState("");
  const [activeFilter, setActiveFilter] = useState("all");
  const [isLoading, setIsLoading] = useState(false);

  const filtered = tokens.filter((t) =>
    (t.name + " " + t.symbol + " " + (t.description || ""))
      .toLowerCase()
      .includes(q.toLowerCase())
  );

  const handleFilterChange = (filter: string) => {
    setIsLoading(true);
    setActiveFilter(filter);
    setTimeout(() => setIsLoading(false), 300);
  };

  if (error) {
    return (
      <div className="premium-container" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '400px' }}>
        <div style={{ fontSize: '48px', marginBottom: '24px' }}>📡</div>
        <h2 style={{ fontSize: '24px', marginBottom: '16px', color: '#ff4d4d' }}>Connection Error</h2>
        <p style={{ color: '#aaa', marginBottom: '8px' }}>Could not connect to the backend server.</p>
        <p style={{ color: '#666', fontSize: '14px' }}>Please ensure the backend is running on port 4000.</p>
        <button
          onClick={() => window.location.reload()}
          style={{
            marginTop: '24px',
            padding: '12px 24px',
            background: 'var(--primary)',
            border: 'none',
            borderRadius: '8px',
            color: 'white',
            cursor: 'pointer'
          }}
        >
          Retry Connection
        </button>
      </div>
    );
  }

  return (
    <div className="premium-container" >
      {/* Title and search */}
      {/* New Discovery Toolbar */}
      <div className="discovery-toolbar">
        <div className="search-group">
          <span className="search-icon-wrapper">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="11" cy="11" r="8" />
              <line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
          </span>
          <input
            className="clean-search-input"
            type="text"
            placeholder="Search tokens..."
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
        </div>

        <div className="filter-separator" />

        <div className="filter-group">
          {["all", "trending", "newest", "watchlist"].map((filter) => (
            <button
              key={filter}
              className={`filter-btn ${activeFilter === filter ? "active" : ""}`}
              onClick={() => handleFilterChange(filter)}
            >
              {filter === "all" && "All"}
              {filter === "trending" && "Trending"}
              {filter === "newest" && "New"}
              {filter === "watchlist" && "Watchlist"}
            </button>
          ))}
        </div>
      </div>

      {/* Tokens grid */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
          gap: "20px",
          width: "100%",
          opacity: isLoading ? 0.6 : 1,
          transition: "opacity 0.3s ease",
        }}
      >
        {filtered.map((t) => (
          <TokenCard
            key={t.id}
            token={t}
            onOpen={() => navigate(`/token/${t.id}`)}
          />
        ))}
        {filtered.length === 0 && (
          <div style={{ gridColumn: "1 / -1", textAlign: "center", padding: "60px", color: "#94a3b8" }}>
            <div style={{ fontSize: "32px", marginBottom: "16px" }}>🔍</div>
            <div style={{ fontSize: "18px", fontWeight: "600", marginBottom: "8px", color: "#e2e8f0" }}>
              No tokens match your search
            </div>
            <div style={{ fontSize: "14px" }}>Try adjusting your search or filters</div>
          </div>
        )}
      </div>
    </div >
  );
}

// Inner App Component that can use wallet hooks
function AppContent() {
  const [route, setRoute] = useState(parseRoute());
  const [tokens, setTokens] = useState<MemeTokenConfig[]>([]);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [connectionError, setConnectionError] = useState(false);

  // Authentication state
  const [user, setUser] = useState<any>(null);
  const [admin, setAdmin] = useState<any>(null);
  const [userSessionToken, setUserSessionToken] = useState<string | null>(null);
  const [adminSessionToken, setAdminSessionToken] = useState<string | null>(null);

  // Use auto-login hook for wallet-based authentication
  useAutoLogin(setUser, setUserSessionToken);

  // Initialize authentication from localStorage
  useEffect(() => {
    const savedUserToken = localStorage.getItem('userSessionToken');
    const savedUserProfile = localStorage.getItem('userData');
    const savedAdminToken = localStorage.getItem('adminSessionToken');
    const savedAdminProfile = localStorage.getItem('adminProfile');

    if (savedUserToken && savedUserProfile) {
      try {
        setUserSessionToken(savedUserToken);
        setUser(JSON.parse(savedUserProfile));
      } catch (err) {
        localStorage.removeItem('userSessionToken');
        localStorage.removeItem('userData');
      }
    }

    if (savedAdminToken && savedAdminProfile) {
      try {
        setAdminSessionToken(savedAdminToken);
        setAdmin(JSON.parse(savedAdminProfile));
      } catch (err) {
        localStorage.removeItem('adminSessionToken');
        localStorage.removeItem('adminProfile');
      }
    }
  }, []);

  // Route change handler
  // Route change handler - History API support
  useEffect(() => {
    const onRoute = () => setRoute(parseRoute());
    window.addEventListener("popstate", onRoute);
    window.addEventListener("pushstate", onRoute);
    return () => {
      window.removeEventListener("popstate", onRoute);
      window.removeEventListener("pushstate", onRoute);
    };
  }, []);

  // Helper to ensure images use the high-performance Arweave gateway
  const formatTokenImage = (url: string) => {
    if (!url) return "";

    // If it's already a full URL that's not from a problematic gateway, return it
    if (url.startsWith('http') && !url.includes('gateway.irys.xyz') && !url.includes('gateway.irys.network')) {
      return url;
    }

    // Extract ID from Arweave/Irys URLs
    if (url.includes('arweave') || url.includes('irys')) {
      const parts = url.split('/');
      const id = parts[parts.length - 1];
      if (id && id.length > 30) {
        // Use the official Irys gateway which is generally most reliable for recent uploads
        return `https://gateway.irys.xyz/${id}`;
      }
    }


    return url;
  };


  // Load tokens from backend with retry mechanism
  const loadTokens = useCallback(async (retryCount = 0) => {
    try {
      const res = await fetch(`${BACKEND_URL}/tokens`);
      const data = await res.json();
      setTokens(
        data.map((t: any) => ({
          id: String(t.id),
          name: t.name,
          symbol: t.symbol,
          description: t.description || "",
          image: formatTokenImage(t.image_url || t.image || ""),
          contractPackageId: t.contract_package_id,
          bondingCurveId: t.bonding_curve_id,
          metadataId: typeof t.metadata_id === "string" && t.metadata_id.length > 0 ? t.metadata_id : undefined,
          coinType: t.coin_type,
          decimals: Number.isFinite(Number(t.decimals)) ? Number(t.decimals) : 9,
          poolCreated: Boolean(t.pool_created),
          poolId: t.pool_id || null,
          swapPlatformFeeObj: t.swap_platform_fee_obj || null,
        }))
      );
      setConnectionError(false);
    } catch (error) {
      console.error('Error loading tokens:', error);
      // Retry up to 3 times with 2 second delays
      if (retryCount < 3) {
        console.log(`Retrying token load in 2 seconds... (attempt ${retryCount + 1}/3)`);
        setTimeout(() => loadTokens(retryCount + 1), 2000);
      } else {
        setConnectionError(true);
      }
    }
  }, []);

  useEffect(() => {
    loadTokens();
  }, [loadTokens]);

  // Authentication handlers
  const handleUserLogin = (userData: any, sessionToken: string) => {
    setUser(userData);
    setUserSessionToken(sessionToken);
    localStorage.setItem('userSessionToken', sessionToken);
    localStorage.setItem('userData', JSON.stringify(userData));
  };

  const handleAdminLogin = (adminData: any, sessionToken: string) => {
    setAdmin(adminData);
    setAdminSessionToken(sessionToken);
    localStorage.setItem('adminSessionToken', sessionToken);
    localStorage.setItem('adminProfile', JSON.stringify(adminData));
    navigate('/admin');
  };

  const handleUserLogout = () => {
    setUser(null);
    setUserSessionToken(null);
    localStorage.removeItem('userSessionToken');
    localStorage.removeItem('userData');
  };

  const handleAdminLogout = () => {
    setAdmin(null);
    setAdminSessionToken(null);
    localStorage.removeItem('adminSessionToken');
    localStorage.removeItem('adminProfile');
    navigate('/');
  };

  const handleProfileUpdate = (profileData: any) => {
    setUser(profileData);
    localStorage.setItem('userData', JSON.stringify(profileData));
  };

  // Authentication pages
  if (route.page === "user-login") {
    return (
      <EnhancedUserLogin
        onLoginSuccess={handleUserLogin}
        onSignupClick={() => navigate('/user-signup')}
        onClose={() => navigate('/')}
      />
    );
  }

  if (route.page === "user-signup") {
    return (
      <EnhancedUserSignup
        onSignupSuccess={handleUserLogin}
        onLoginClick={() => navigate('/user-login')}
        onClose={() => navigate('/')}
      />
    );
  }

  if (route.page === "user-dashboard" && user && userSessionToken) {
    return (
      <UserDashboard
        user={user}
        sessionToken={userSessionToken}
        onProfileUpdate={handleProfileUpdate}
        onLogout={handleUserLogout}
        onNavigate={navigate}
      />
    );
  } else if (route.page === "user-dashboard") {
    navigate('/user-login');
    return null;
  }

  if (route.page === "admin-login") {
    return (
      <EnhancedAdminLogin
        onLoginSuccess={handleAdminLogin}
        onClose={() => navigate('/')}
      />
    );
  }

  if (route.page === "admin" && admin && adminSessionToken) {
    return (
      <AdminDashboard
        admin={admin}
        sessionToken={adminSessionToken}
        onLogout={handleAdminLogout}
      />
    );
  } else if (route.page === "admin") {
    navigate('/admin-login');
    return null;
  }

  // Main app layout
  const sidebarWidth = sidebarCollapsed ? "84px" : "240px";

  return (
    <div
      className={`App ${sidebarCollapsed ? "sidebar-collapsed" : ""}`}
      style={{ "--sidebar-width": sidebarWidth } as React.CSSProperties}
    >
      <EnhancedNavbar
        currentPage={route.page}
        onNavigate={navigate}
        sidebarCollapsed={sidebarCollapsed}
        onToggleSidebar={() => setSidebarCollapsed(!sidebarCollapsed)}
        user={user}
        onUserLogin={() => navigate('/user-login')}
        onUserLogout={handleUserLogout}
        onProfileUpdate={handleProfileUpdate}
      />
      <TrendingTicker
        tokens={tokens}
        onTokenClick={(tokenId) => navigate(`/token/${tokenId}`)}
      />
      <main className="enhanced-content">
        {route.page === "home" && <TokenFeed tokens={tokens} error={connectionError} />}
        {route.page === "create" && (
          <EnhancedCreateTokenForm onCreated={loadTokens} />
        )}
        {route.page === "swap" && (
          <div style={{ margin: "-24px", minHeight: "100vh" }}>
            <EnhancedSwapPage
              tokens={tokens}
              user={user}
              userSessionToken={userSessionToken}
              onNavigate={navigate}
            />
          </div>
        )}
        {route.page === "token" && (() => {
          const tokenForRoute = tokens.find((t) => t.id === route.id);
          if (tokenForRoute) {
            return (
              <div style={{ margin: "-24px", minHeight: "100vh" }}>
                <EnhancedTradingInterface
                  token={tokenForRoute}
                  user={user}
                  userSessionToken={userSessionToken}
                />
              </div>
            );
          } else {
            return <div className="card">Token not found.</div>;
          }
        })()}
      </main>
    </div>
  );
}

// Main App wrapper
function App() {
  return (
    <ThemeProvider>
      <WalletKitProvider>
        <AppContent />
      </WalletKitProvider>
    </ThemeProvider>
  );
}

export default App;
